import { cacheKeys } from "@constants/cache-keys";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import useAuthUser from "./useAuthUser";

const DISTRIBUTION_TYPES = {
	COMMON: "COMMON_DISTRIBUTION",
	PRACTICE_TEST: "GET_PRATICETEST_DISTRIBUTION",
	STUDY_PACK: "GET_STUDYPACK_DISTRIBUTION",
} as const;

type Params = {
	productId?: string;
};
const useInvalidateQueries = (params?: Params) => {
	const queryClient = useQueryClient();
	const { userPublicMetadataId } = useAuthUser();

	const {
		currentExamContext: { studyPackCollectionId, examId },
	} = useExamContext();

	const invalidateDistribution = useCallback(
		(distributionType: string) => {
			const queryKey = [
				cacheKeys.home,
				userPublicMetadataId,
				distributionType,
				examId,
				...(studyPackCollectionId ? [studyPackCollectionId] : []),
			];
			queryClient.invalidateQueries({
				queryKey,
			});
		},
		[queryClient, userPublicMetadataId, studyPackCollectionId, examId],
	);

	const invalidateProductMetadata = useCallback(() => {
		if (params?.productId) {
			queryClient.invalidateQueries({
				queryKey: [cacheKeys.productMetadata, params.productId],
			});
		}
	}, [queryClient, params?.productId]);

	const invalidateCommonDistribution = useCallback(
		() => invalidateDistribution(DISTRIBUTION_TYPES.COMMON),
		[invalidateDistribution],
	);

	const invalidatePracticeTestDistribution = useCallback(
		() => invalidateDistribution(DISTRIBUTION_TYPES.PRACTICE_TEST),
		[invalidateDistribution],
	);

	const invalidateStudyPackDistribution = useCallback(
		() => invalidateDistribution(DISTRIBUTION_TYPES.STUDY_PACK),
		[invalidateDistribution],
	);

	const invalidateAllQueries = useCallback(() => {
		invalidateProductMetadata();
		invalidateCommonDistribution();
		invalidatePracticeTestDistribution();
		invalidateStudyPackDistribution();
	}, [
		invalidateProductMetadata,
		invalidateCommonDistribution,
		invalidatePracticeTestDistribution,
		invalidateStudyPackDistribution,
	]);

	return {
		invalidateProductMetadata,
		invalidateCommonDistribution,
		invalidatePracticeTestDistribution,
		invalidateStudyPackDistribution,
		invalidateAllQueries,
	};
};

export default useInvalidateQueries;
